import { localGet, localSet, localRemove, sessionGet, sessionSet, sessionRemove, } from '@square/safe-storage';
import { set, get, remove } from 'js-cookie';
import { isOneTrustCategoryConsented, onOneTrustConsentChanged, } from './onetrust-handler';
import { cookieSet, localStorageSet, sessionStorageSet, } from './utils/set-store';
/**
 * A generic wrapper for the js-cookie getter
 * @param name The cookie to get
 */
export function getCookie(name) {
    return get(name);
}
/**
 * A wrapper around js-cookie set. This will ensure that the OneTrust category
 * passed in is consented to before setting the cookie, otherwise it will ignore it.
 *
 * If the item is not in the consented categories but is already set, we will clear it out.
 *
 * It also listens for subsequent consent changes. If the user changes consent, we will clear out the item,
 * as it should not be set at this point in time.
 *
 * @param name The name of the cookie.
 * @param value The value of the cookie.
 * @param category The OneTrust category of the cookie
 * @param options Optional cookie options
 */
export function setCookie(name, value, category, options) {
    if (!isOneTrustCategoryConsented(category)) {
        clearCookieIfExists(name);
        return;
    }
    set(name, value, options);
    cookieSet.add(name);
    // Listen for changes so that we clear if consent changes
    if (cookieSet.has(name)) {
        onOneTrustConsentChanged(() => {
            if (!isOneTrustCategoryConsented(category)) {
                clearCookieIfExists(name);
            }
        });
    }
}
/**
 * A generic wrapper around the js-cookie remove function
 * @param name The name of the cookie
 */
export function removeCookie(name) {
    remove(name);
}
/**
 * A helper to check if the cookie exists
 * @param name The name of the cookie
 */
export function hasCookie(name) {
    return get(name) !== undefined;
}
function clearCookieIfExists(name) {
    if (hasCookie(name)) {
        removeCookie(name);
    }
}
/**
 * A wrapper around the @square/safe-storage localSet function. It will ensure the
 * localStorage item being set has its category consented to beforehand.
 *
 * If the item is not in the consented categories but is already set, we will clear it out.
 *
 * It also listens for subsequent consent changes. If the user changes consent, we will clear out the item,
 * as it should not be set at this point in time.
 * @param name The item to set
 * @param value The value of the local storage item
 * @param category The OneTrust category of the item
 */
export function setLocalStorageItem(name, value, category) {
    if (!isOneTrustCategoryConsented(category)) {
        clearLocalStorageItemIfExists(name);
        return;
    }
    localSet(name, value);
    localStorageSet.add(name);
    // Listen for changes so that we clear if consent changes
    if (localStorageSet.has(name)) {
        onOneTrustConsentChanged(() => {
            if (!isOneTrustCategoryConsented(category)) {
                clearLocalStorageItemIfExists(name);
            }
        });
    }
}
/**
 * A helper to check if the local storage item exists
 * @param name
 */
export function hasLocalStorageItem(name) {
    return localGet(name) !== undefined;
}
/**
 * A generic wrapper around the @square/safe-storage localGet function
 * @param name The name of the local storage item
 */
export function getLocalStorageItem(name) {
    return localGet(name);
}
/**
 * Clears the local storage item if it exists
 * @param name The name of the local storage item
 */
function clearLocalStorageItemIfExists(name) {
    if (hasLocalStorageItem(name)) {
        localRemove(name);
    }
}
/**
 * A wrapper around the @square/safe-storage sessionSet function. It will ensure the
 * sessionStorage item being set has its category consented to beforehand.
 *
 * If the item is not in the consented categories but is already set, we will clear it out.
 *
 * It also listens for subsequent consent changes. If the user changes consent, we will clear out the item,
 * as it should not be set at this point in time.
 * @param name The item to set
 * @param value The value of the session storage item
 * @param category The OneTrust category of the item
 */
export function setSessionStorageItem(name, value, category) {
    if (!isOneTrustCategoryConsented(category)) {
        clearSessionStorageItemIfExists(name);
        return;
    }
    sessionSet(name, value);
    sessionStorageSet.add(name);
    // Listen for changes so that we clear if consent changes
    if (sessionStorageSet.has(name)) {
        onOneTrustConsentChanged(() => {
            if (!isOneTrustCategoryConsented(category)) {
                clearSessionStorageItemIfExists(name);
            }
        });
    }
}
/**
 * Determines if the session storage item exists
 * @param name The name of the session storage item
 */
export function hasSessionStorageItem(name) {
    return sessionGet(name) !== undefined;
}
/**
 * A generic wrapper around the @square/safe-storage sessionGet function
 * @param name The name of the session storage item
 */
export function getSessionStorageItem(name) {
    return sessionGet(name);
}
function clearSessionStorageItemIfExists(name) {
    if (hasSessionStorageItem(name)) {
        sessionRemove(name);
    }
}
//# sourceMappingURL=compliance-storage.js.map